import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import animeConfig from '../config/animeConfig';
import characterProfiles from '../data/characterProfiles';
import characterMeta from './characterMeta';
// wiki images blocked by hotlink protection, using MAL images instead
import './Guides.css';
import './CharacterProfile.css';
import './ArcsPage.css';

const CharacterProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const char = characterProfiles.find(c => c.slug === slug);
  const currentIndex = characterProfiles.findIndex(c => c.slug === slug);
  const prevChar = currentIndex > 0 ? characterProfiles[currentIndex - 1] : null;
  const nextChar = currentIndex < characterProfiles.length - 1 ? characterProfiles[currentIndex + 1] : null;

  const { title: metaTitle, description: metaDescription } = characterMeta(char);

  const bgStyle = {
    backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.85)), url(${animeConfig.onePiece.backgroundImage})`,
  };

  // Scroll to top when character changes
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!char) {
    return (
      <div className="guide-page-wrapper" style={bgStyle}>
        <div className="guide-container" style={{ textAlign: 'center', paddingTop: '80px' }}>
          <h1>Character Not Found</h1>
          <p>This character page doesn't exist.</p>
          <Link to="/characters" className="guide-cta-button">Browse All Characters</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        {/* People search "<name> one piece", not "<name> character profile", and
            they are after the bounty or the Devil Fruit. The title says the name,
            the series and what the page holds; the description leads with the
            two facts they came for. */}
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://www.onepiecetrivia.com/characters/${char.slug}`} />
        <meta property="og:title" content={`${char.name} - One Piece Character`} />
        <meta property="og:description" content={char.bio.slice(0, 160)} />
        <meta property="og:url" content={`https://www.onepiecetrivia.com/characters/${char.slug}`} />
        <meta property="og:image" content={char.image} />
      </Helmet>

      <div className="guide-page-wrapper" style={bgStyle}>
        <div className="guide-container">

          {/* Navigation tabs */}

          {/* Breadcrumb */}
          <div className="char-breadcrumb">
            <Link to="/characters">All Characters</Link> / {char.name}
          </div>

          {/* Hero section */}
          <div className="char-hero">
            <img
              src={char.image}
              alt={`${char.name} from One Piece`}
              className="char-hero-image"
              loading="lazy"
            />
            <div className="char-hero-info">
              <h1>{char.name}</h1>
              {char.aliases.length > 0 && (
                <div className="char-aliases">
                  {char.aliases.map(a => <span key={a} className="char-alias-tag">{a}</span>)}
                </div>
              )}
              <div className="char-stats">
                <div className="char-stat">
                  <span className="char-stat-label">Crew</span>
                  <span className="char-stat-value">{char.crew}</span>
                </div>
                <div className="char-stat">
                  <span className="char-stat-label">Role</span>
                  <span className="char-stat-value">{char.role}</span>
                </div>
                {char.devilFruit && char.devilFruit !== 'None' && (
                  <div className="char-stat">
                    <span className="char-stat-label">Devil Fruit</span>
                    <span className="char-stat-value">{char.devilFruit}</span>
                  </div>
                )}
                {char.devilFruitType && (
                  <div className="char-stat">
                    <span className="char-stat-label">Fruit Type</span>
                    <span className="char-stat-value">{char.devilFruitType}</span>
                  </div>
                )}
                {char.bounty && (
                  <div className="char-stat char-stat-bounty">
                    <span className="char-stat-label">Bounty</span>
                    <span className="char-stat-value">{char.bounty} berries</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="guide-entry">
            <h2 style={{ marginTop: 0 }}>About</h2>
            <p>{char.bio}</p>
          </div>

          {/* Key Facts */}
          {char.details && char.details.length > 0 && (
            <div className="guide-entry">
              <h2 style={{ marginTop: 0 }}>Key Facts</h2>
              <ul className="char-facts-list">
                {char.details.map((fact, i) => (
                  <li key={i}>{fact}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Quiz CTA buttons */}
          <div className="char-quiz-buttons">
            <h2>Test Your Knowledge</h2>
            <div className="char-quiz-row">
              <button onClick={() => navigate('/quiz')} className="char-quiz-btn char-quiz-btn-char">
                <img src="/character-quiz-and-any-related.png" alt="" className="char-quiz-btn-icon" />
                Character Quiz
              </button>
              <button onClick={() => navigate('/image-quiz')} className="char-quiz-btn char-quiz-btn-image">
                <img src="/image-quiz-and-any-related.png" alt="" className="char-quiz-btn-icon" />
                Image Quiz
              </button>
              <button onClick={() => navigate('/devil-fruit-quiz')} className="char-quiz-btn char-quiz-btn-fruit">
                <img src="/devil-fruit-quiz-and-any-related.png" alt="" className="char-quiz-btn-icon" />
                Devil Fruit Quiz
              </button>
              <button onClick={() => navigate('/bounty-battle')} className="char-quiz-btn">
                <span className="char-quiz-btn-icon game-emoji" aria-hidden="true">💰</span>
                Bounty Battle
              </button>
              <button onClick={() => navigate('/puzzle')} className="char-quiz-btn">
                <span className="char-quiz-btn-icon game-emoji" aria-hidden="true">🧩</span>
                Jigsaw Puzzle
              </button>
            </div>
          </div>

          {/* Prev / Next navigation */}
          <div className="char-nav">
            {prevChar ? (
              <Link to={`/characters/${prevChar.slug}`} className="char-nav-link char-nav-prev">
                &larr; {prevChar.name}
              </Link>
            ) : <span />}
            {nextChar ? (
              <Link to={`/characters/${nextChar.slug}`} className="char-nav-link char-nav-next">
                {nextChar.name} &rarr;
              </Link>
            ) : <span />}
          </div>

        </div>
      </div>
    </>
  );
};

export default CharacterProfile;
